/**
 * Unified Sync Helper Service
 *
 * Provides consistent debounced sync to Firestore with:
 * - Configurable debounce timing
 * - Single-key and multi-key support
 * - Automatic sync status tracking
 * - Error handling with retry logic (GRO-35)
 * - Toast notifications for user feedback
 * - Cache updates
 */

import { useSyncStore } from '../stores/useSyncStore';
import { useToastStore } from '../stores/useToastStore';
import { withRetry, categorizeError } from './firestoreErrorHandler';

// Store all active timeouts
const timeouts = new Map();

// Default debounce timings
export const SYNC_TIMING = {
    FAST: 500,      // Quick settings changes
    NORMAL: 1000,   // Most data (tasks, preferences)
    SLOW: 2000,     // Typing-heavy (journal)
};

/**
 * Create a debounced sync function
 *
 * @param {string} syncKey - Unique key for this sync operation (e.g., 'preferences', 'task-{id}')
 * @param {function} firestoreFn - Async function that saves to Firestore
 * @param {function} cacheFn - Optional function to update cache after successful sync
 * @param {number} debounceMs - Debounce timing in milliseconds
 * @returns {function} - Debounced sync function
 *
 * @example
 * // Single-key sync (preferences)
 * const syncPrefs = createDebouncedSync(
 *     'preferences',
 *     async (data) => await firestoreService.updateUserPreferences(userId, data),
 *     (data) => cacheService.updateUser({ preferences: data })
 * );
 * syncPrefs(preferencesData);
 *
 * @example
 * // Multi-key sync (tasks by list ID)
 * const syncTaskList = (taskListId, taskList) => {
 *     createDebouncedSync(
 *         `task-${taskListId}`,
 *         async () => await firestoreService.updateTaskList(userId, taskListId, taskList),
 *         () => cacheService.updateTaskList(taskListId, taskList)
 *     )();
 * };
 */
export const createDebouncedSync = (
    syncKey,
    firestoreFn,
    cacheFn = null,
    debounceMs = SYNC_TIMING.NORMAL
) => {
    return async (...args) => {
        // Clear existing timeout for this key
        if (timeouts.has(syncKey)) {
            clearTimeout(timeouts.get(syncKey));
        }

        // Signal sync starting
        useSyncStore.getState().startSync();

        // Set new debounced timeout
        const timeout = setTimeout(async () => {
            timeouts.delete(syncKey);
            try {
                // Use retry logic for resilient syncing (GRO-35)
                await withRetry(
                    () => firestoreFn(...args),
                    { operationName: syncKey, silent: true }
                );
                if (cacheFn) {
                    cacheFn(...args);
                }
                useSyncStore.getState().completeSync();
            } catch (error) {
                console.error(`Sync failed for ${syncKey}:`, error);
                const { userMessage } = categorizeError(error);
                useSyncStore.getState().failSync(userMessage);
                // Show toast notification for persistent errors (GRO-35)
                useToastStore.getState().error(userMessage, {
                    action: 'Retry',
                    onAction: () => createDebouncedSync(syncKey, firestoreFn, cacheFn, 0)(...args),
                });
            }
        }, debounceMs);

        timeouts.set(syncKey, timeout);
    };
};

/**
 * Sync immediately without debounce (for create/delete operations)
 *
 * @param {string} syncKey - Key for logging purposes
 * @param {function} firestoreFn - Async function that saves to Firestore
 * @param {function} cacheFn - Optional function to update cache
 */
export const syncImmediate = async (syncKey, firestoreFn, cacheFn = null) => {
    useSyncStore.getState().startSync();
    try {
        // Use retry logic for resilient syncing (GRO-35)
        await withRetry(
            firestoreFn,
            { operationName: syncKey, silent: true }
        );
        if (cacheFn) {
            cacheFn();
        }
        useSyncStore.getState().completeSync();
    } catch (error) {
        console.error(`Sync failed for ${syncKey}:`, error);
        const { userMessage } = categorizeError(error);
        useSyncStore.getState().failSync(userMessage);
        // Show toast notification for persistent errors (GRO-35)
        useToastStore.getState().error(userMessage);
        throw error; // Re-throw so caller can handle
    }
};

/**
 * Cancel pending sync for a specific key
 * Useful when deleting items that have pending syncs
 *
 * @param {string} syncKey - Key to cancel
 */
export const cancelSync = (syncKey) => {
    if (timeouts.has(syncKey)) {
        clearTimeout(timeouts.get(syncKey));
        timeouts.delete(syncKey);
    }
};

/**
 * Cancel all pending syncs (useful on logout)
 */
export const cancelAllSyncs = () => {
    timeouts.forEach((timeout) => clearTimeout(timeout));
    timeouts.clear();
};

/**
 * Load data with cache-first strategy
 *
 * @param {function} cacheFn - Function to get from cache, returns data or null
 * @param {function} firestoreFn - Async function to get from Firestore
 * @param {function} updateCacheFn - Optional function to update cache after Firestore load
 * @returns {Promise<any>} - The loaded data
 */
export const loadWithCache = async (cacheFn, firestoreFn, updateCacheFn = null) => {
    // Try cache first
    const cachedData = cacheFn();
    if (cachedData !== null && cachedData !== undefined) {
        return cachedData;
    }

    // Load from Firestore
    const data = await firestoreFn();

    // Update cache if we got data
    if (data && updateCacheFn) {
        updateCacheFn(data);
    }

    return data;
};

/**
 * Initialize store data from Firestore with standard pattern
 *
 * @param {object} options
 * @param {function} options.getCached - Get data from cache
 * @param {function} options.getFromFirestore - Get data from Firestore
 * @param {function} options.onData - Callback when data is loaded (cached or fresh)
 * @param {function} options.onError - Callback on error
 * @param {function} options.updateCache - Optional: update cache after Firestore load
 */
export const initializeFromFirestore = async ({
    getCached,
    getFromFirestore,
    onData,
    onError,
    updateCache = null,
}) => {
    try {
        // Try cache first
        const cachedData = getCached?.();
        if (cachedData) {
            onData(cachedData, { fromCache: true });
            return;
        }

        // Load from Firestore
        const data = await getFromFirestore();
        if (data) {
            if (updateCache) {
                updateCache(data);
            }
            onData(data, { fromCache: false });
        } else {
            onData(null, { fromCache: false });
        }
    } catch (error) {
        console.error('Failed to initialize from Firestore:', error);
        onError?.(error);
    }
};
